import React, {FC, useCallback} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {useClipboard} from '../../../../../../libs/shared/ui/src/hooks/clipboardHookHelpers';
import {textSelection} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {
  buildCopyLinkDataTestId,
  generateConversationPermalink,
} from '../../../../../../srcCommon/helpers/premalinksHelpers';
import {useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {buildConversationPublicId} from '../../../../../helpers/models/conversationHelpers';
import {ConversationModel} from '../../../../../models/api/conversationModel';
import {PublicId} from '../../../../../models/applications/publicIdTypes';
import {getCompanyPreferences} from '../../../../../store/data/company/companySelectors';
import {AnimatedCheckmarkButton} from '../../../../common/buttons/animatedCheckmarkButton';
import {ConversationSummaryDetailsSubsection} from './conversationSummaryDetailsSubsection';

/*
 * Props.
 */

interface ConversationSummaryConversationIdProps {
  conversation: ConversationModel;
  host: string;
}

/*
 * Styles.
 */

const StyledConversationIdContainerDiv = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto;
  grid-column-gap: 10px;
  margin-top: -10px;
`;

const StyledConversationIdSpan = styled.span`
  display: inline-flex;
  align-items: center;
  ${textSelection()};
`;

/*
 * Component.
 */

export const ConversationSummaryConversationId: FC<ConversationSummaryConversationIdProps> = (props) => {
  const {conversation, host} = props;
  const {copy} = useClipboard();
  const preferences = useAppSelector(getCompanyPreferences);

  const publicId = buildConversationPublicId(conversation);
  const copyLinkTestId = buildCopyLinkDataTestId('conversation', preferences);

  const onCopyPublicIdClick = useCallback(() => copy(publicId), [copy, publicId]);
  const onCopyPublicLinkClick = useCallback(async () => {
    copy(generateConversationPermalink(conversation, host, preferences));
  }, [conversation, copy, host, preferences]);

  return (
    <ConversationSummaryDetailsSubsection
      title={<Trans id="conversation-summary-id">Conversation ID</Trans>}
      content={renderConversationIdContent(
        publicId,
        copyLinkTestId,
        onCopyPublicIdClick,
        onCopyPublicLinkClick,
      )}
    />
  );
};

function renderConversationIdContent(
  publicId: PublicId<'conversation'>,
  copyLinkTestId: string,
  onCopyPublicIdClick: () => void,
  onCopyPublicLinkClick: () => void,
) {
  return (
    <StyledConversationIdContainerDiv>
      <StyledConversationIdSpan>{publicId}</StyledConversationIdSpan>

      <AnimatedCheckmarkButton onClick={onCopyPublicIdClick} interactionId="conversationSummaryCopyIdButton">
        <Trans id="conversation-summary-copyid">Copy ID</Trans>
      </AnimatedCheckmarkButton>

      <AnimatedCheckmarkButton
        onClick={onCopyPublicLinkClick}
        interactionId="conversationSummaryCopyLinkButton"
        data-testid={copyLinkTestId}
      >
        <Trans id="conversation-summary-copylink">Copy link</Trans>
      </AnimatedCheckmarkButton>
    </StyledConversationIdContainerDiv>
  );
}
